// Quiet scroll motion: content below the first screen fades in and rises as
// it scrolls into view, once. Styles are in nta-theme.css.
//
// What moves: the blocks inside each section (headings, text, images) and,
// in product and collection grids, each card on its own, a beat apart. Section
// backgrounds stay put, so a Charcoal band never fades in over Cream.
// Carousels reveal as one row, since cards scrolled off to the side would
// otherwise never come into view.
//
// Anything on screen at load is left alone, so the page never looks blank.
// Nothing moves for reduced motion or in the theme editor, and without this
// script everything simply shows as normal.

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = Boolean(window.Shopify?.designMode);

const ITEMS = '.resource-list__item, .product-grid__item';
const SKIP = '.section-background, template, script, style, link';
const STAGGER_MS = 100;
const MAX_STAGGER_STEPS = 4;

if (!reducedMotion && !designMode && 'IntersectionObserver' in window) {
  /** Elements still waiting to reveal, and the cards each carousel holds */
  const pending = new Set();
  const carousels = new Map();

  const isRendered = (element) => element.getClientRects().length > 0;
  const isBelowFold = (element) => element.getBoundingClientRect().top >= window.innerHeight;

  const reveal = (element, step = 0) => {
    if (!pending.delete(element)) return;
    const delay = Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS;
    if (delay) element.style.transitionDelay = `${delay}ms`;
    element.classList.add('nta-reveal--in');

    // Drop the classes once settled, so nothing keeps a transform (a
    // transform would trap fixed-position pieces such as drawers)
    const settle = (event) => {
      if (event.target !== element || event.propertyName !== 'transform') return;
      element.removeEventListener('transitionend', settle);
      element.classList.remove('nta-reveal', 'nta-reveal--in');
      element.style.transitionDelay = '';
    };
    element.addEventListener('transitionend', settle);
  };

  const revealTarget = (target, step) => {
    observer.unobserve(target);
    const cards = carousels.get(target);
    if (!cards) return reveal(target, step);
    carousels.delete(target);
    cards.forEach((card, index) => reveal(card, step + index));
  };

  const byPosition = (a, b) => {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    return ra.top - rb.top || ra.left - rb.left;
  };

  // Things arriving together (a heading and its row of cards) go in reading
  // order, a beat apart
  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort(byPosition)
        .forEach((target, step) => revealTarget(target, step));
    },
    { rootMargin: '0px 0px -20% 0px' }
  );

  const watch = (element) => {
    if (!isRendered(element) || !isBelowFold(element)) return;
    element.classList.add('nta-reveal');
    pending.add(element);
    observer.observe(element);
  };

  for (const section of document.querySelectorAll('#MainContent > .shopify-section')) {
    const grid = section.querySelector('.section');
    if (!grid) continue;

    // Section content, except wrappers around a grid (their cards move instead)
    for (const child of grid.children) {
      if (child.matches(SKIP) || child.querySelector(ITEMS)) continue;
      watch(child);
    }

    for (const card of section.querySelectorAll(ITEMS)) {
      const carousel = card.closest('.resource-list__carousel');
      if (!carousel) {
        watch(card);
        continue;
      }
      if (!isRendered(carousel) || !isBelowFold(carousel)) continue;
      card.classList.add('nta-reveal');
      pending.add(card);
      if (!carousels.has(carousel)) {
        carousels.set(carousel, []);
        observer.observe(carousel);
      }
      carousels.get(carousel).push(card);
    }
  }

  // Catch-up: content near the very end of the page may never reach the
  // trigger line, and jumping down the page can skip past content
  let queued = false;
  const catchUp = () => {
    queued = false;
    const atBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4;
    const targets = [...pending, ...carousels.keys()].filter(
      (target) => target.isConnected && (atBottom || target.getBoundingClientRect().bottom < 0)
    );
    targets.sort(byPosition).forEach((target) => revealTarget(target, 0));
    if (pending.size === 0) window.removeEventListener('scroll', onScroll);
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(catchUp);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}
